import { asWidgetProps, BucketFormikInput, BucketFormikInputProps } from 'Src/types/widget-helpers';
import React, { Fragment, useMemo } from 'react';
import * as yup from 'yup';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
export const subscriptionSchema =
  (displayEmailInput: boolean, displayPhoneInput: boolean, locale: string) => () =>
    yup.object().shape({
      civility: yup.string().required(t('common.form.errors.requiredField')),
      firstName: yup
        .string()
        .max(
          20,
          t('common.form.errors.fieldTooLong', {
            label: t('common.form.labels.firstName'),
            maxlength: 20,
          }),
        ) // regex to validate first / last name - no special characters, only letters, dashes, dots or apostrophs are allowed
        .matches(
          /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
          t('common.form.errors.noSpecialCharsField'),
        )
        .required(t('common.form.errors.requiredField')),
      lastName: yup
        .string()
        .max(
          30,
          t('common.form.errors.fieldTooLong', {
            label: t('common.form.labels.lastName'),
            maxlength: 30,
          }),
        ) // regex to validate first / last name - no special characters, only letters, dashes, dots or apostrophs are allowed
        .matches(
          /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
          t('common.form.errors.noSpecialCharsField'),
        )
        .required(t('common.form.errors.requiredField')),
      unicodeFirstName: yup.string().max(100),
      unicodeLastName: yup.string().max(100),
      email: yup.string().when([], {
        is: () => displayEmailInput,
        then: yup
          .string()
          .nullable()
          .when('phone', {
            is: (phone) => !phone,
            then: yup
              .string()
              .email(t('common.form.errors.invalidEmailField'))
              .required(t('common.form.errors.requiredField')),
            otherwise: yup.string().notRequired(),
          }),
      }),
      phone: yup
        .number()
        .nullable()
        .when([], {
          is: () => displayPhoneInput,
          then: yup.number().when('email', {
            is: (email) => !email,
            then: yup.number().required(t('common.form.errors.requiredField')),
            otherwise: yup.number().notRequired(),
          }),
        }),
      // add conditional validation if legalAgreement is locale is russian
      legalAgreement:
        locale === 'ru-RU'
          ? yup.boolean().oneOf([true], t('booking.participants.errorMessage.legalAgreement'))
          : yup.boolean().notRequired().nullable(),
    });
export const NewsletterForm = () => {
  const appContext = useAppContext();
  const { locale } = appContext;
  const [phoneInputProps, emailInputProps] = useMemo(
    () => [
      isFeatureFlipped(appContext)('newsletter.displayPhoneInput') && {
        label: t('newsletter.signup.mobileNumber'),
        name: 'phone',
        type: 'tel',
      },
      isFeatureFlipped(appContext)('newsletter.displayEmailInput') && {
        label: t('booking.participants.labels.email'),
        name: 'email',
        placeholder: t('common.form.inputFormats.email'),
        type: 'email',
      },
    ],
    [locale],
  );
  return (
    <Fragment>
      <div className="-mx-10 md:flex">
        <div className="w-full px-10 md:w-1/3">
          <BucketFormikInput
            {...asWidgetProps<BucketFormikInputProps>({
              isRequired: true,
              label: t('booking.participants.labels.civility'),
              name: 'civility',
              options: [
                {
                  isDisabled: true,
                  name: t('booking.participants.labels.civility'),
                  value: '',
                },
                {
                  name: t('newsletter.signup.civilityMale'),
                  value: 'M',
                },
                {
                  name: t('newsletter.signup.civilityFemale'),
                  value: 'F',
                },
              ],
              type: 'select',
            })}
          />
        </div>
        <div className="mt-15 w-full px-10 md:mt-0 md:w-1/3">
          <BucketFormikInput
            {...asWidgetProps<BucketFormikInputProps>({
              isRequired: true,
              label: t('common.form.labels.firstName'),
              name: 'firstName',
              placeholder: t('common.form.labels.firstName'),
              type: 'text',
            })}
          />
        </div>
        <div className="mt-15 w-full px-10 md:mt-0 md:w-1/3">
          <BucketFormikInput
            {...asWidgetProps<BucketFormikInputProps>({
              isRequired: true,
              label: t('common.form.labels.lastName'),
              name: 'lastName',
              placeholder: t('common.form.labels.lastName'),
              type: 'text',
            })}
          />
        </div>
      </div>
      {appContext.displayUnicodeFields &&
        isFeatureFlipped(appContext)('newsletter.displayUnicodeFields') && (
          <div className="-mx-10 mt-15 md:flex">
            <div className="w-full px-10 md:w-1/3">
              <BucketFormikInput
                {...asWidgetProps<BucketFormikInputProps>({
                  label: t('common.form.labels.unicodeFirstName'),
                  name: 'unicodeFirstName',
                  placeholder: t('common.form.labels.unicodeFirstName'),
                  type: 'text',
                })}
              />
            </div>
            <div className="mt-15 w-full px-10 md:mt-0 md:w-1/3">
              <BucketFormikInput
                {...asWidgetProps<BucketFormikInputProps>({
                  label: t('common.form.labels.unicodeLastName'),
                  name: 'unicodeLastName',
                  placeholder: t('common.form.labels.unicodeLastName'),
                  type: 'text',
                })}
              />
            </div>
          </div>
        )}
      {phoneInputProps && (
        <div className="mt-15">
          <BucketFormikInput {...asWidgetProps<BucketFormikInputProps>(phoneInputProps)} />
        </div>
      )}
      {emailInputProps && (
        <div className="mt-15">
          <BucketFormikInput {...asWidgetProps<BucketFormikInputProps>(emailInputProps)} />
        </div>
      )}
    </Fragment>
  );
};
